import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Grid, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { getUsersByCompany } from 'services/firebaseServices';
import { getProfile } from 'utilities';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { fireStoreDB } from 'services';

export default function CardTracking() {
  const [employees, setEmployees] = useState([]);
  const [businessData, setBusinessData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState({});
  const [bulkSending, setBulkSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const profile = getProfile();
        if (!profile?.uid) return;
        const q = query(collection(fireStoreDB, 'businesses'), where('userId', '==', profile.uid));
        const snap = await getDocs(q);
        if (!snap.empty) {
          const biz = snap.docs[0].data();
          setBusinessData(biz);
          const result = await getUsersByCompany(biz.companyName);
          setEmployees(result);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const sendReminder = async (emp) => {
    setSending((prev) => ({ ...prev, [emp.id]: true }));
    const signUpUrl = (() => {
      if (!businessData?.companyName) return window.location.origin;
      const slug = businessData.companyName.toLowerCase().replace(/\s+/g, '-');
      return `${window.location.origin}/sign-up/${slug}`;
    })();
    try {
      await fetch('/api/send-invite-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          toEmail: emp.email,
          firstName: emp.firstName,
          companyName: businessData?.companyName || '',
          signUpUrl,
          subject: 'Reminder: Order your World Medical Card',
          customBody: `Your World Medical Card membership is active but you haven't ordered your physical card yet. Log in to your profile and click "Order Card" to receive it.`,
        }),
      });
    } catch (err) {
      console.error(err);
    } finally {
      setSending((prev) => ({ ...prev, [emp.id]: false }));
    }
  };

  const sendBulkReminders = async () => {
    const notOrdered = employees.filter((e) => e.cardOrderStatus !== 'Registered');
    if (notOrdered.length === 0) return;
    setBulkSending(true);
    setResult(null);
    let sent = 0; let failed = 0;
    for (const emp of notOrdered) {
      try { await sendReminder(emp); sent++; } catch { failed++; }
    }
    setBulkSending(false);
    setResult({ sent, failed });
  };

  const ordered = employees.filter((e) => e.cardOrderStatus === 'Registered');
  const notOrdered = employees.filter((e) => e.cardOrderStatus !== 'Registered');
  const reordered = employees.filter((e) => e.cardReOrderStatus === 'Registered');

  return (
    <Box sx={{ width: '100%', p: { xs: 1, md: 2 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1f2937' }}>Card Order Tracking</Typography>
          <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
            Track who has ordered their physical World Medical Card and send reminders to those who haven&apos;t.
          </Typography>
        </Box>
        {notOrdered.length > 0 && (
          <Button
            variant="contained"
            startIcon={bulkSending ? <CircularProgress size={16} color="inherit" /> : <MailOutlineIcon />}
            disabled={bulkSending}
            onClick={sendBulkReminders}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' } }}
          >
            Remind All ({notOrdered.length})
          </Button>
        )}
      </Box>

      {result && (
        <Alert severity={result.failed === 0 ? 'success' : 'warning'} sx={{ mb: 3, borderRadius: 2 }} onClose={() => setResult(null)}>
          {result.sent} reminder{result.sent !== 1 ? 's' : ''} sent{result.failed > 0 ? `, ${result.failed} failed` : ''}.
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[
          { icon: <CreditCardIcon />, label: 'Total Employees', value: employees.length, color: '#3b82f6' },
          { icon: <CheckCircleIcon />, label: 'Card Ordered', value: ordered.length, color: '#10b981' },
          { icon: <HourglassEmptyIcon />, label: 'Not Yet Ordered', value: notOrdered.length, color: '#f59e0b' },
          { icon: <CreditCardIcon />, label: 'Card Reordered', value: reordered.length, color: '#8b5cf6' },
        ].map(({ icon, label, value, color }) => (
          <Grid item xs={12} sm={6} md={3} key={label}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
                {React.cloneElement(icon, { sx: { color, fontSize: 22 } })}
              </Box>
              <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>{label}</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1f2937', lineHeight: 1 }}>
                {loading ? <CircularProgress size={24} /> : value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e5e7eb' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', mb: 2 }}>Employee Card Status</Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : employees.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <CreditCardIcon sx={{ fontSize: 48, color: '#d1d5db', mb: 1 }} />
            <Typography color="text.secondary">No employees found.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {['Name', 'Email', 'Card Ordered', 'Order Date', 'Card Reordered', 'Reorder Date', 'Action'].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 600, bgcolor: '#f9fafb', color: '#374151', border: 'none' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {employees.map((emp) => {
                  const hasOrdered = emp.cardOrderStatus === 'Registered';
                  const hasReordered = emp.cardReOrderStatus === 'Registered';
                  return (
                    <TableRow key={emp.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                      <TableCell sx={{ fontWeight: 600, color: '#1f2937' }}>{emp.firstName} {emp.lastName}</TableCell>
                      <TableCell sx={{ color: '#6b7280' }}>{emp.email}</TableCell>
                      <TableCell>
                        <Chip label={hasOrdered ? 'Ordered' : 'Not Ordered'} color={hasOrdered ? 'success' : 'default'} size="small" variant={hasOrdered ? 'filled' : 'outlined'} sx={{ fontWeight: 600 }} />
                      </TableCell>
                      <TableCell sx={{ color: '#6b7280' }}>{emp.cardOrderDate || '-'}</TableCell>
                      <TableCell>
                        <Chip label={hasReordered ? 'Reordered' : '-'} color={hasReordered ? 'primary' : 'default'} size="small" variant={hasReordered ? 'filled' : 'outlined'} sx={{ fontWeight: 600 }} />
                      </TableCell>
                      <TableCell sx={{ color: '#6b7280' }}>{emp.cardReOrderDate || '-'}</TableCell>
                      <TableCell>
                        {!hasOrdered && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={sending[emp.id] ? <CircularProgress size={12} /> : <MailOutlineIcon />}
                            disabled={!!sending[emp.id]}
                            onClick={() => sendReminder(emp)}
                            sx={{ textTransform: 'none', borderRadius: 2, fontSize: 12 }}
                          >
                            Remind
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}
